import React, { useState } from "react";
import { Event } from "../../types";
import { useGroupNavigation } from "../../hooks/useGroupNavigation";
import { useEvent } from "../../contexts/EventContext";
import {
  GiftIcon,
  InfoIcon,
  CheckCircleIcon,
  LockIcon,
  WarningCircleIcon,
} from "@phosphor-icons/react";
import { SurpriseEventHelpModal } from "../common";
import "./surprise-event-banner.css";

interface SurpriseEventBannerProps {
  event: Event;
  hasActiveList: boolean;
  isOnlyUser: boolean; // true se è l'unico con la lista
  className?: string;
}

const SurpriseEventBanner: React.FC<SurpriseEventBannerProps> = ({
  event,
  hasActiveList,
  isOnlyUser,
  className = "",
}) => {
  const { navigateToMyLists } = useGroupNavigation();
  const { userListStatus } = useEvent();
  const [showHelpModal, setShowHelpModal] = useState(false);

  if (!event.isSurprise) {
    return null;
  }

  const renderListStatus = () => {
    if (!hasActiveList || !userListStatus) {
      return null;
    }

    if (userListStatus.isLocked) {
      return (
        <div className="list-status published">
          <LockIcon size={14} weight="fill" />
          <span>La tua lista è pubblicata</span>
        </div>
      );
    } else if (userListStatus.canPublish) {
      return (
        <div className="list-status ready">
          <CheckCircleIcon size={14} weight="fill" />
          <span>La tua lista è pronta per la pubblicazione</span>
        </div>
      );
    } else {
      return (
        <div className="list-status draft">
          <WarningCircleIcon size={14} weight="fill" />
          <span>La tua lista è vuota</span>
        </div>
      );
    }
  };

  const renderContent = () => {
    if (!hasActiveList) {
      // Caso 3: Non ho una lista per questo evento
      return (
        <div className="banner-content">
          <div className="banner-info">
            <div className="banner-icon">
              <GiftIcon size={20} weight="fill" />
            </div>
            <div className="banner-text">
              <strong>Evento a sorpresa!</strong>
              <p>I destinatari non potranno vedere cosa gli è stato regalato.</p> 
            </div>
          </div>
          <div className="banner-actions">
            <button
              className="help-button"
              onClick={() => setShowHelpModal(true)}
              title="Come funziona?"
            >
              <InfoIcon size={16} weight="fill" />
              Come funziona?
            </button>
          </div>
        </div>
      );
    }

    if (isOnlyUser) {
      // Caso 1: Sono l'unico con la lista
      return (
        <div className="banner-content">
          <div className="banner-info">
            <div className="banner-icon">
              <GiftIcon size={20} weight="fill" />
            </div>
            <div className="banner-text">
              <strong>Evento a sorpresa!</strong>
              <p>Non potrai vedere quali regali sono stati prenotati</p>
              {renderListStatus()}
            </div>
          </div>
          <div className="banner-actions">
            <button className="banner-cta" onClick={() => navigateToMyLists()}>
              Gestisci lista
            </button>
            <button
              className="help-button"
              onClick={() => setShowHelpModal(true)}
              title="Come funziona?"
            >
              <InfoIcon size={16} weight="fill" />
              Come funziona?
            </button>
          </div>
        </div>
      );
    } else {
      // Caso 2: Faccio parte dei membri con la lista
      return (
        <div className="banner-content">
          <div className="banner-info">
            <div className="banner-icon">
              <GiftIcon size={20} weight="fill" />
            </div>
            <div className="banner-text">
              <strong>Evento a sorpresa!</strong>
              <p>
                Non potrai vedere quali regali sono stati prenotati e gli altri non
                potranno vedere cosa gli è stato regalato.
              </p>
              {renderListStatus()}
            </div>
          </div>
          <div className="banner-actions">
            <button className="banner-cta" onClick={() => navigateToMyLists()}>
              Gestisci lista
            </button>
            <button
              className="help-button"
              onClick={() => setShowHelpModal(true)}
              title="Come funziona?"
            >
              <InfoIcon size={16} weight="fill" />
              Come funziona?
            </button>
          </div>
        </div>
      );
    }
  };

  return (
    <>
      <div className={`surprise-event-banner ${className}`}>
        {renderContent()}
      </div>

      {/* Modal di aiuto */}
      <SurpriseEventHelpModal
        isOpen={showHelpModal}
        onClose={() => setShowHelpModal(false)}
      />
    </>
  );
};

export default SurpriseEventBanner;
